/**
 * Movimientos: la lista de todo lo que ha pasado.
 *
 * El **período** lo manda la cabecera y vale para todas las pantallas a la vez: día,
 * semana, mes, año, todo el historial o un rango elegido a mano. Aquí quedan los
 * filtros propios de la lista (tipo, categoría, cuenta y búsqueda) y la paginación.
 *
 * Cada movimiento se puede borrar (a la papelera) y restaurar, y **borrar devuelve
 * el dinero al saldo solo**, porque el saldo se calcula y no se guarda. Eso es lo
 * que en Cuentia no ocurría: borrar un gasto dejaba el saldo descontado para siempre.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Search,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  TRANSACTION_KINDS,
  formatDateLabel,
  kindLabel,
  relativeDayLabel,
  signedAmountForKind,
  type TransactionKind,
} from '@eldescuadre/core';

import {
  deleteTransaction,
  deleteTransactionsBulk,
  getAccounts,
  getCategories,
  getTransactions,
  restoreTransaction,
  restoreTransactionsBulk,
} from '../lib/endpoints';
import { usePeriod } from '../lib/period';
import { Card, EmptyState, ErrorNote, Loading, Money } from '../components/ui';

const PAGE_SIZE = 25;

export function MovementsPage(): React.JSX.Element {
  const queryClient = useQueryClient();

  const [kinds, setKinds] = useState<TransactionKind[]>([]);
  const [categoryId, setCategoryId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showTrash, setShowTrash] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  /*
   * El período lo manda la cabecera, y vale para todas las pantallas a la vez.
   *
   * Antes esta pantalla tenía su propio mes, así que se podía estar mirando marzo
   * en Movimientos y abril en Informes sin que nada lo advirtiera. Y solo existía el
   * mes: no se podía ver un día suelto, ni un año entero, ni un rango a mano.
   */
  const range = usePeriod((s) => s.range);

  /**
   * Los movimientos marcados para una acción en lote.
   *
   * Se vacía al cambiar de página, de período o de pestaña de la papelera: dejar
   * marcado algo que ya no se ve llevaría a borrar cosas que el usuario no tiene
   * delante, que es exactamente lo que no debe pasar con un borrado en lote.
   */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkResult, setBulkResult] = useState<{
    action: 'delete' | 'restore';
    count: number;
    notFound: number;
  } | null>(null);

  const filter = useMemo(
    () => ({
      // El rango se manda al servidor, que es quien filtra de verdad. Así no se
      // descarga todo el historial para descartarlo en el navegador.
      from: range.from,
      to: range.to,
      ...(kinds.length > 0 ? { kinds } : {}),
      ...(categoryId ? { categoryIds: [categoryId] } : {}),
      ...(accountId ? { accountIds: [accountId] } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(showTrash ? { includeDeleted: true } : {}),
      page,
      pageSize: PAGE_SIZE,
    }),
    [range.from, range.to, kinds, categoryId, accountId, search, page, showTrash],
  );

  // Al cambiar de período se vuelve a la primera página: quedarse en la página 7 de
  // una lista que ahora tiene 2 deja la pantalla vacía sin explicación.
  useEffect(() => {
    setPage(1);
  }, [range.from, range.to]);

  const transactions = useQuery({
    queryKey: ['transactions', filter],
    queryFn: () => getTransactions(filter),
  });

  const categories = useQuery({ queryKey: ['categories'], queryFn: () => getCategories() });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => getAccounts() });

  const remove = useMutation({
    mutationFn: deleteTransaction,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
        queryClient.invalidateQueries({ queryKey: ['summary'] }),
        queryClient.invalidateQueries({ queryKey: ['accounts'] }),
      ]);
    },
  });

  const restore = useMutation({
    mutationFn: restoreTransaction,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
        queryClient.invalidateQueries({ queryKey: ['summary'] }),
        queryClient.invalidateQueries({ queryKey: ['accounts'] }),
      ]);
    },
  });

  /** La acción en lote: borrar o restaurar varios de una vez. */
  const bulk = useMutation({
    mutationFn: async ({ action, ids }: { action: 'delete' | 'restore'; ids: string[] }) => {
      const result =
        action === 'delete'
          ? await deleteTransactionsBulk(ids)
          : await restoreTransactionsBulk(ids);

      return {
        action,
        count: action === 'delete' ? (result as { deleted: number }).deleted : (result as { restored: number }).restored,
        notFound: result.notFound,
      };
    },
    onSuccess: async (result) => {
      setBulkResult(result);
      setSelected(new Set());
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
        queryClient.invalidateQueries({ queryKey: ['summary'] }),
        queryClient.invalidateQueries({ queryKey: ['accounts'] }),
      ]);
    },
    onError: () => setBulkResult(null),
  });

  // La selección se vacía al cambiar de página, de período o de la papelera. Dejar
  // marcado algo que ya no se ve llevaría a borrar cosas que el usuario no tiene
  // delante, y eso es exactamente lo que no debe pasar con un borrado en lote.
  useEffect(() => {
    setSelected(new Set());
    setBulkResult(null);
  }, [page, range.from, range.to, showTrash]);

  const categoryName = useMemo(() => {
    const map = new Map<string, string>();
    for (const category of categories.data?.categories ?? []) map.set(category.id, category.name);
    return map;
  }, [categories.data]);

  const accountName = useMemo(() => {
    const map = new Map<string, string>();
    for (const account of accounts.data?.accounts ?? []) map.set(account.id, account.name);
    return map;
  }, [accounts.data]);

  const totalPages = transactions.data ? Math.ceil(transactions.data.total / PAGE_SIZE) : 1;

  /** Indica si un filtro está activo, para poder limpiarlos de una vez. */
  const hasFilters = kinds.length > 0 || categoryId !== '' || accountId !== '' || search.trim() !== '';

  const clearFilters = (): void => {
    setKinds([]);
    setCategoryId('');
    setAccountId('');
    setSearch('');
    setPage(1);
  };

  return (
    <div className="space-y-4 animate-ed-fade-in">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Movimientos</h1>
          <p className="mt-0.5 text-sm capitalize text-ed-text-muted">{range.label}</p>
        </div>
        <Link to="/nuevo" className="ed-btn-primary !min-h-[44px] !w-auto shrink-0 !px-4">
          Registrar
        </Link>
      </header>

      {/* El período se cambia arriba, en la cabecera, y vale para todas las
          pantallas. Aquí solo queda el botón de filtros. */}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          className={`ed-btn-ghost !w-auto !px-3 ${hasFilters ? 'text-ed-gold' : ''}`}
          onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters}
          aria-label="Filtros"
        >
          <SlidersHorizontal size={18} />
        </button>
      </div>

      {/* Búsqueda */}
      <div className="relative">
        <input
          className="ed-input pl-9"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Buscar por comercio o nota…"
          aria-label="Buscar movimientos"
          autoComplete="off"
        />
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ed-text-muted"
          aria-hidden="true"
        />
      </div>

      {showFilters && (
        <Card title="Filtros">
          <div className="space-y-4">
            <div>
              <p className="ed-label mb-2">Tipo</p>
              <div className="flex flex-wrap gap-2">
                {TRANSACTION_KINDS.map((kind) => {
                  const active = kinds.includes(kind);
                  return (
                    <button
                      key={kind}
                      type="button"
                      aria-pressed={active}
                      onClick={() => {
                        setKinds((current) =>
                          active ? current.filter((k) => k !== kind) : [...current, kind],
                        );
                        setPage(1);
                      }}
                      className={`min-h-[40px] rounded-full px-3 text-xs font-medium ${
                        active
                          ? 'bg-ed-gold-imperial text-ed-bg-abyss'
                          : 'border border-ed-bg-border/50 text-ed-text-silver'
                      }`}
                    >
                      {kindLabel(kind)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="space-y-1.5">
                <span className="ed-label block">Categoría</span>
                <select
                  className="ed-input"
                  value={categoryId}
                  onChange={(e) => {
                    setCategoryId(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">Todas</option>
                  {(categories.data?.categories ?? []).map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-1.5">
                <span className="ed-label block">Cuenta</span>
                <select
                  className="ed-input"
                  value={accountId}
                  onChange={(e) => {
                    setAccountId(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">Todas</option>
                  {(accounts.data?.accounts ?? []).map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showTrash}
                  onChange={(e) => {
                    setShowTrash(e.target.checked);
                    setPage(1);
                  }}
                  className="h-4 w-4 rounded border-ed-bg-border"
                />
                Ver también los borrados
              </label>

              {hasFilters && (
                <button
                  type="button"
                  className="text-xs text-ed-cyan-light hover:underline"
                  onClick={clearFilters}
                >
                  Quitar todos los filtros
                </button>
              )}
            </div>
          </div>
        </Card>
      )}

      {remove.isError && <ErrorNote message="No se pudo borrar el movimiento." />}
      {restore.isError && <ErrorNote message="No se pudo restaurar el movimiento." />}

      {transactions.isLoading && <Loading />}

      {transactions.data && transactions.data.items.length === 0 && (
        <Card>
          <EmptyState
            title={hasFilters ? 'Ningún movimiento coincide' : 'Este mes no hay movimientos'}
            message={
              hasFilters
                ? 'Prueba a quitar algún filtro o a buscar en otro mes.'
                : 'Cuando registres algo, aparecerá aquí con su fecha y su categoría.'
            }
            action={
              hasFilters ? (
                <button type="button" className="ed-btn-secondary !w-auto !px-4" onClick={clearFilters}>
                  Quitar los filtros
                </button>
              ) : (
                <Link to="/nuevo" className="ed-btn-primary !w-auto !px-4">
                  Registrar un movimiento
                </Link>
              )
            }
          />
        </Card>
      )}

      {transactions.data && transactions.data.items.length > 0 && (
        <>
          {/* La barra de selección. Solo aparece cuando hay algo marcado, para no
              ocupar sitio en el caso normal, que es mirar sin tocar nada. */}
          {selected.size > 0 && (
            <div className="sticky top-16 z-20 flex flex-wrap items-center gap-3 rounded-ed border border-ed-gold/40 bg-ed-bg-card px-3 py-2.5 shadow-ed-card">
              <span className="text-sm font-medium">
                {selected.size} {selected.size === 1 ? 'marcado' : 'marcados'}
              </span>

              <button
                type="button"
                className="text-xs text-ed-cyan-light hover:underline"
                onClick={() => setSelected(new Set())}
              >
                Quitar la selección
              </button>

              <span className="ml-auto flex gap-2">
                {showTrash ? (
                  <button
                    type="button"
                    className="ed-btn-secondary !min-h-[38px] !w-auto !px-3 text-xs"
                    disabled={bulk.isPending}
                    onClick={() => bulk.mutate({ action: 'restore', ids: [...selected] })}
                  >
                    <RotateCcw size={14} aria-hidden="true" />
                    Restaurar
                  </button>
                ) : (
                  <button
                    type="button"
                    className="ed-btn-danger !min-h-[38px] !w-auto !px-3 text-xs"
                    disabled={bulk.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `¿Mandar ${selected.size} ${selected.size === 1 ? 'movimiento' : 'movimientos'} a la papelera? Se pueden recuperar después.`,
                        )
                      ) {
                        bulk.mutate({ action: 'delete', ids: [...selected] });
                      }
                    }}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                    {bulk.isPending ? 'Borrando…' : 'Borrar'}
                  </button>
                )}
              </span>
            </div>
          )}

          {/* El resultado del lote. Se dicen los dos números: si se pidieron diez y
              solo se pudieron siete, el usuario tiene que saberlo. */}
          {bulkResult && (
            <p className="rounded-ed border border-ed-cyan/40 bg-ed-cyan/10 px-3 py-2 text-sm text-ed-cyan-light">
              {bulkResult.action === 'delete'
                ? `Se mandaron ${bulkResult.count} a la papelera.`
                : `Se restauraron ${bulkResult.count}.`}
              {bulkResult.notFound > 0 && (
                <>
                  {' '}
                  {bulkResult.notFound}{' '}
                  {bulkResult.notFound === 1 ? 'no se encontró' : 'no se encontraron'} (puede que
                  ya no estuvieran).
                </>
              )}
            </p>
          )}

          <Card>
            <ul className="divide-y divide-ed-bg-border/25">
              {transactions.data.items.map((transaction) => {
                const isDeleted = transaction.deletedAt !== null;
                const isIncome =
                  transaction.kind === 'INCOME' || transaction.kind === 'SPLIT_SETTLEMENT';

                return (
                  <li
                    key={transaction.id}
                    className={`flex items-center gap-3 py-2.5 ${isDeleted ? 'opacity-45' : ''}`}
                  >
                    {/* La casilla de marcar. Solo se puede marcar lo que la acción del
                        momento puede manejar: en la papelera se restauran, fuera de
                        ella se borran. */}
                    <input
                      type="checkbox"
                      className="h-4 w-4 shrink-0 rounded border-ed-bg-border"
                      checked={selected.has(transaction.id)}
                      disabled={isDeleted !== showTrash && selected.size === 0}
                      aria-label={`Marcar ${transaction.merchant || kindLabel(transaction.kind)}`}
                      onChange={(e) => {
                        setSelected((current) => {
                          const next = new Set(current);
                          if (e.target.checked) next.add(transaction.id);
                          else next.delete(transaction.id);
                          return next;
                        });
                      }}
                    />

                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                        isIncome
                          ? 'bg-ed-success/15 text-ed-success'
                          : 'bg-ed-bg-elevated text-ed-text-muted'
                      }`}
                      aria-hidden="true"
                    >
                      {isIncome ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">
                        {transaction.merchant || kindLabel(transaction.kind)}
                        {isDeleted && (
                          <span className="ml-2 rounded bg-ed-coral-deep/30 px-1.5 py-0.5 text-[10px] uppercase text-ed-coral-light">
                            borrado
                          </span>
                        )}
                      </span>

                      <span className="block truncate text-xs text-ed-text-muted">
                        {relativeDayLabel(transaction.date) ?? formatDateLabel(transaction.date)}
                        {' · '}
                        {kindLabel(transaction.kind)}
                        {transaction.categoryId && categoryName.has(transaction.categoryId) && (
                          <> · {categoryName.get(transaction.categoryId)}</>
                        )}
                        {transaction.accountId && accountName.has(transaction.accountId) && (
                          <> · {accountName.get(transaction.accountId)}</>
                        )}
                      </span>

                      {transaction.items.length > 0 && (
                        <span className="mt-0.5 block truncate text-xs text-ed-text-muted">
                          {transaction.items
                            .map((item) => `${item.description} (${item.amount})`)
                            .join(' · ')}
                        </span>
                      )}
                    </span>

                    <span className="flex shrink-0 flex-col items-end gap-1">
                      {/*
                        El importe con su signo: el gasto en negativo y el ingreso en
                        positivo, para que una lista larga se lea de un vistazo sin
                        mirar la etiqueta del tipo. Es la misma función que usa el
                        exportador, así que el archivo y la pantalla dicen lo mismo.
                      */}
                      <Money
                        amount={signedAmountForKind(transaction.kind, transaction.amount)}
                        signed
                        className="text-sm font-semibold"
                      />
                      {isDeleted ? (
                        <button
                          type="button"
                          onClick={() => restore.mutate(transaction.id)}
                          className="text-xs text-ed-cyan-light hover:underline"
                          aria-label={`Restaurar ${transaction.merchant ?? kindLabel(transaction.kind)}`}
                        >
                          <RotateCcw size={13} className="mr-1 inline" aria-hidden="true" />
                          Restaurar
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            if (
                              window.confirm(
                                'Se enviará a la papelera y el saldo se corregirá solo. ¿Seguimos?',
                              )
                            ) {
                              remove.mutate(transaction.id);
                            }
                          }}
                          className="text-xs text-ed-text-muted hover:text-ed-coral-light"
                          aria-label={`Borrar ${transaction.merchant ?? kindLabel(transaction.kind)}`}
                        >
                          <Trash2 size={13} aria-hidden="true" />
                        </button>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Card>

          <div className="flex items-center justify-between gap-3 text-sm">
            <p className="text-ed-text-muted">
              {transactions.data.total}{' '}
              {transactions.data.total === 1 ? 'movimiento' : 'movimientos'}
            </p>

            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="ed-btn-ghost !w-auto !px-3"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Página anterior"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="text-xs text-ed-text-muted">
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  className="ed-btn-ghost !w-auto !px-3"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  aria-label="Página siguiente"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <p className="px-1 pb-2 text-center text-xs text-ed-text-muted">
        Lo que borras va a la papelera y se puede recuperar. Mientras tanto, deja de contar en los
        saldos y en los informes.
      </p>
    </div>
  );
}
